import React, { useEffect, useMemo, useState } from 'react';
import { Instagram, Play, ArrowUpRight, Heart, MessageCircle } from 'lucide-react';
import { api } from '@/api';

// Fallback bento tiles used ONLY while the feed is loading or empty.
const FALLBACK_TILES = [
  { img: 'https://images.pexels.com/photos/10126663/pexels-photo-10126663.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',  span: 'md:col-span-2 md:row-span-2', label: 'Fleet livery · Sprinter',   tag: 'INSTALL' },
  { img: 'https://images.pexels.com/photos/10126657/pexels-photo-10126657.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',  span: '',                          label: 'Full color change · Coupe', tag: 'WRAP' },
  { img: 'https://images.unsplash.com/photo-1646531840695-62810bcd1171?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxjYXIlMjB3cmFwJTIwaW5zdGFsbGF0aW9ufGVufDB8fHx8MTc4NTgwMDYyNnww&ixlib=rb-4.1.0&q=85', span: '', label: 'Panel prep', tag: 'PROCESS' },
  { img: 'https://images.unsplash.com/photo-1535448580089-c7f9490c78b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxzbGVlayUyMHNwb3J0cyUyMGNhcnxlbnwwfHx8fDE3ODU4MDA2MjZ8MA&ixlib=rb-4.1.0&q=85',       span: 'md:col-span-2', label: 'Satin black · Complete', tag: 'REVEAL' },
  { img: 'https://images.pexels.com/photos/17370583/pexels-photo-17370583.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', span: '', label: 'Carbon fiber accent', tag: 'DETAIL' },
  { img: 'https://images.unsplash.com/photo-1666555038185-8323c553de64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxicnVzaGVkJTIwbWV0YWwlMjBkYXJrJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODU4MDA2MjZ8MA&ixlib=rb-4.1.0&q=85', span: '', label: 'Brushed metal wall wrap', tag: 'INTERIOR' },
];

const IG_URL = 'https://www.instagram.com/motorinks/';
const TT_URL = 'https://www.tiktok.com/@motorinks';

const SOURCE_TAG = {
  Instagram: { label: 'INSTAGRAM', icon: Instagram },
  Facebook:  { label: 'FACEBOOK',  icon: Instagram },
  TikTok:    { label: 'TIKTOK',    icon: Play },
  YouTube:   { label: 'YOUTUBE',   icon: Play },
};

function PostTile({ p, isHero }) {
  const src = SOURCE_TAG[p.source] || { label: (p.source || 'POST').toUpperCase(), icon: Instagram };
  const Icon = src.icon;
  return (
    <a
      href={p.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`portfolio-post-${p.id}`}
      className={`group relative overflow-hidden border mi-line-strong bg-[#0A0A0A] ${isHero ? 'md:col-span-2 md:row-span-2' : ''}`}
    >
      {p.image && (
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
          style={{ backgroundImage: `url(${p.image})` }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent opacity-90 group-hover:opacity-70 transition-opacity" />

      <div className="absolute top-3 left-3 flex items-center gap-2">
        <span className="mi-tag text-[9px] inline-flex items-center gap-1">
          <Icon className="w-3 h-3" /> {src.label}
        </span>
        {p.video && <span className="mi-tag text-[9px] mi-tag-red">REEL</span>}
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end justify-between gap-3">
        <div className="min-w-0">
          {p.caption && (
            <div className={`mi-headline text-white leading-tight line-clamp-2 ${isHero ? 'text-xl md:text-2xl' : 'text-sm md:text-base'}`}>
              {p.caption}
            </div>
          )}
          <div className="mi-eyebrow text-[9px] text-white/60 mt-1 flex items-center gap-3">
            {typeof p.like_count === 'number' && (
              <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" /> {p.like_count}</span>
            )}
            {typeof p.comment_count === 'number' && (
              <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {p.comment_count}</span>
            )}
          </div>
        </div>
        <ArrowUpRight className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
      </div>
    </a>
  );
}

export default function Portfolio() {
  const [posts, setPosts] = useState(null); // null=loading, []=empty
  useEffect(() => {
    api.get('/portfolio/public')
      .then(({ data }) => setPosts(data.items || []))
      .catch(() => setPosts([]));
  }, []);

  const hasFeed = Array.isArray(posts) && posts.length > 0;

  const grid = useMemo(() => {
    if (!hasFeed) return null;
    const withImages = posts.filter((p) => p.image);
    return withImages.slice(0, 9);
  }, [posts, hasFeed]);

  return (
    <section id="portfolio" className="relative py-24 lg:py-32 border-b mi-line">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <div className="mi-eyebrow mb-3">// PORTFOLIO · BEFORE / AFTER</div>
            <h2 className="mi-headline text-5xl md:text-6xl lg:text-7xl">
              Shot on the shop floor.<br />
              <span className="text-[#E63946]">Posted everywhere.</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <a href={IG_URL} target="_blank" rel="noopener noreferrer" data-testid="portfolio-instagram-link" className="mi-btn-ghost">
              <Instagram className="w-4 h-4" /> @motorinks
            </a>
            <a href={TT_URL} target="_blank" rel="noopener noreferrer" data-testid="portfolio-tiktok-link" className="mi-btn-ghost">
              <Play className="w-4 h-4" /> TikTok
            </a>
          </div>
        </div>

        {grid ? (
          <div className="grid grid-cols-2 md:grid-cols-4 grid-flow-dense gap-4 auto-rows-[180px] md:auto-rows-[220px]" data-testid="portfolio-grid">
            {grid.map((p, i) => <PostTile key={p.id} p={p} isHero={i === 0} />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 grid-flow-dense gap-4 auto-rows-[180px] md:auto-rows-[220px]">
            {FALLBACK_TILES.map((t, i) => (
              <a
                key={i}
                href={IG_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`portfolio-tile-${i}`}
                className={`group relative overflow-hidden border mi-line-strong ${t.span}`}
              >
                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{ backgroundImage: `url(${t.img})` }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent opacity-90 group-hover:opacity-70 transition-opacity" />
                <div className="absolute top-3 left-3">
                  <span className="mi-tag text-[9px]">{t.tag}</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end justify-between">
                  <div>
                    <div className="mi-headline text-lg md:text-xl text-white leading-tight">{t.label}</div>
                    <div className="mi-eyebrow text-[9px] text-white/60 mt-1 flex items-center gap-1">
                      <Instagram className="w-3 h-3" /> VIEW ON INSTAGRAM
                    </div>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </a>
            ))}
          </div>
        )}

        <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border mi-line-strong p-5">
          <div>
            <div className="mi-eyebrow mb-1">// FOLLOW ALONG</div>
            <div className="mi-headline text-2xl">We post every project — before, during, after.</div>
          </div>
          <div className="flex gap-3">
            <a href={IG_URL} target="_blank" rel="noopener noreferrer" className="mi-btn-red">
              <Instagram className="w-4 h-4" /> Follow on Instagram
            </a>
            <a href={TT_URL} target="_blank" rel="noopener noreferrer" className="mi-btn-ghost">
              <Play className="w-4 h-4" /> Watch on TikTok
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
